<template>
  <div class="tab-pane active" id="step5">
    
    <!-- selected printable article display and configuration -->
    <template v-for="article in selectedPrintableArticles" :key="article.id">
      <printable-item :article="article" @saveBasket="emit('saveBasket')"></printable-item>
    </template>

    <!-- selected booth refinement configurations display -->
    <div class="printable-article">
      <div class="printable-article__body">
        <h3 class="mt-4" :title="$t('configurator.headings.lettering')">
          {{ $t('configurator.headings.lettering') }}
        </h3>
      </div>
      <div style="display: flex; flex-direction: column;" class="mb-4">
        <configurator-optional-booth-refinement-list/>
      </div>
    </div>

    <!-- add printable article modal button -->
    <div v-if="availablePrintableArticles.length > 0 && !isExhibitionClosed" class="text-center mt-4">
      <button class="btn btn-primary" @click="showModal">
        {{ $t('configurator.buttons.add_more_printable_articles') }}
      </button>
    </div>
    <div class="row" v-if="!isExhibitionClosed">
      <div class="col-md-12">
        <hr>
        <div class="wizard-footer">
          <div class="pull-right">
            <a href="#" v-show="printForEveryArticleSelected && uploadsCompleteForEveryArticle">
              <input
                id="printToSummary"
                type='button'
                class='btn btn-next btn-warning'
                name='next'
                :value='($t("tab.summary") + " " + $t("buttons.next_to"))'
                @click="store.updateCurrentTab('summary')" />
            </a>
            <button v-if="printForEveryArticleSelected && !uploadsCompleteForEveryArticle"
              type="button"
              class="btn btn-warning"
              @click="confirmLaterUploads">
              {{ $t("tab.summary") + " " + $t("buttons.next_to") }}
            </button>
            <button v-if="!printForEveryArticleSelected"
              type="button"
              class="btn btn-warning"
              @click="confirmPrintlessArticles">
              {{ $t("tab.summary") + " " + $t("buttons.next_to") }}
            </button>
          </div>
          <div class="pull-left">
            <input
              type='button'
              class='btn btn-previous btn-warning btn-back-summary'
              name='previous'
              :value='($t("buttons.prev_to") + " " + $t("tab.articles"))'
              @click="store.updateCurrentTab('articles')" />
          </div>
          <div class="clearfix"></div>
        </div>
      </div>
    </div>
		
		<add-printable-article-modal v-if="isAddModalActive"
      :articles="availablePrintableArticles"
      @add="addPrintableArticles"
      @close="hideModal">
    </add-printable-article-modal>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useMainStore } from '@/store'
import { useI18n } from 'vue-i18n'
import _ from 'lodash'
import Swal from 'sweetalert2'
import { ARTICLE_TYPE_PRINTABLE } from '@/constants'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
// define emits
const emit = defineEmits(['resetDisplayedBooth', 'saveBasket'])
// define reactive props
const isAddModalActive = ref(false)
// define computed props
const isExhibitionClosed = computed(() => store.tmp.is_exhibition_closed)
const daysToGo = computed(() => store.tmp.days_to_go)
const basket = computed(() => store.settings.basket)
const shoppingCart = computed(() => store.settings.shopping_cart)

const selectedPrintableArticles = computed(() => {
  if (basket.value === null) {
    return []
  }

  return basket.value.selectedArticles.filter((article) => {
    return article.type === ARTICLE_TYPE_PRINTABLE && article.count > 0
  })
})

const availablePrintableArticles = computed(() => {
  const selectedArticles = shoppingCart.value.items.map(article => article.id)

  if (basket.value === null) {
    return []
  }

  return _.cloneDeep(basket.value.selectedArticles.filter((article) => {
    if (!article.pivot.bookable || daysToGo.value <= article.pivot.bookable_till_in_days) {
      return false
    }

    return article.type === ARTICLE_TYPE_PRINTABLE && !selectedArticles.includes(article.id)
  }))
})

const printForEveryArticleSelected = computed(() => {
  const count = shoppingCart.value.print_items.reduce((a, b) => {
    if (b.count === 0 && b.article_print_id !== 0) {
      a++
    }

    return a
  }, 0)

  return count === 0
})

const uploadsCompleteForEveryArticle = computed(() => {
  if (!printForEveryArticleSelected.value) {
    return false
  }

  let complete = 0
  let needed = 0
  shoppingCart.value.print_configuration.forEach((configuration) => {
    needed++
    if (configuration.file) {
      complete++
    }
  })

  return complete === needed
})

// define methods
const showModal = () => {
  isAddModalActive.value = true
}

const hideModal = () => {
  isAddModalActive.value = false
}

const addPrintableArticles = (articles) => {
  if (articles.length > 0) {
    articles.forEach(article => {
      const index = basket.value.selectedArticles.findIndex((item) => item.id === article.id)
      if (index > -1) {
        store.settings.basket.selectedArticles[index].count = article.count
        // article does not need to be cloned here, because it was already cloned for the add modal
        shoppingCart.value.addArticle(article)
        if (article.type === ARTICLE_TYPE_PRINTABLE) {
          addPrintArticle(article)
        }
      }
    })

    emit('saveBasket')
  }
  hideModal()
}

const addPrintArticle = (article) => {
	for (const printArticle of article.prints ?? []) {
		const index = basket.value.selectedArticles.findIndex((item) => item.id === printArticle.print_article_id)
		if (index > -1) {
			const clonedPrintArticle = _.cloneDeep(basket.value.selectedArticles[index])
			shoppingCart.value.addPrintArticle(clonedPrintArticle, printArticle)
		} else {
			console.warn('no print article found')
		}
	}
}

const confirmPrintlessArticles = () => {
  Swal.fire({
    title: t('configurator.notices.titles.articles_without_print'),
    text: t('configurator.notices.messages.articles_without_print'),
    showCancelButton: true,
    confirmButtonText: t('configurator.buttons.yes'),
    cancelButtonText: t('configurator.buttons.no'),
  })
    .then((result) => {
      if (result.isConfirmed) {
        goToSummary()
      }
    })
}

const confirmLaterUploads = () => {
  Swal.fire({
    title: t('configurator.notices.titles.missing_graphic_files'),
    text: t('configurator.notices.messages.missing_graphic_files'),
    showCancelButton: true,
    confirmButtonText: t('configurator.buttons.yes'),
    cancelButtonText: t('configurator.buttons.no'),
  })
    .then((result) => {
      if (result.isConfirmed) {
        goToSummary()
      }
    })
}

const goToSummary = () => {
	emit('resetDisplayedBooth', 'summary')
  $('#printToSummary').click()
}
</script>
